import { CommonModule } from '@angular/common';
import { Component, OnInit, ElementRef, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { EmpresaService } from '../../../shared/services/apis/usuario/empresa.service';
import { FciService } from '../../../shared/services/apis/motor-fiscal/fci.service';
import { FciListStruct } from '@shared/services/structs/motor-fiscal/fci-list.struct';

@Component({
  selector: 'app-fci-lista',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
    EmptyListComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './fci-lista.component.html',
  styleUrl: './fci-lista.component.css',
})
export class FciListaComponent implements OnInit {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_Fci;

  public isLoading = false;
  public itens: FciListStruct[] = [];
  public displayedColumns = ['select', 'numero', 'data', 'protocolo', 'mercadorias', 'acoes'];

  public totalRegistros = 0;
  public paginaAtual = 1;
  public tamanhoPagina = 20;
  public ultimaPagina = false;

  public readonly nomesMeses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  public mes: number = new Date().getMonth() + 1;
  public ano: number = new Date().getFullYear();
  public seletorAberto = false;
  public anoSeletor: number = this.ano;

  public selecionados: Set<number> = new Set();

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private alertService: AlertService,
    private fciService: FciService,
    private empresaService: EmpresaService,
    private authService: AuthService,
    private elementRef: ElementRef,
  ) { }

  ngOnInit(): void {
    this.carregar();
  }

  @HostListener('document:mousedown', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (this.seletorAberto && !this.elementRef.nativeElement.contains(event.target)) {
      this.seletorAberto = false;
    }
  }

  public get mesAnoLabel(): string {
    return `${this.nomesMeses[this.mes - 1]} ${this.ano}`;
  }

  public navegarMes(passos: number): void {
    let novoMes = this.mes + passos;
    let novoAno = this.ano;
    if (novoMes < 1) { novoMes = 12; novoAno--; }
    else if (novoMes > 12) { novoMes = 1; novoAno++; }
    this.mes = novoMes;
    this.ano = novoAno;
    this.recarregarDoInicio();
  }

  public abrirSeletor(): void {
    this.anoSeletor = this.ano;
    this.seletorAberto = !this.seletorAberto;
  }

  public navegarAnoSeletor(passos: number): void {
    this.anoSeletor += passos;
  }

  public selecionarMes(mes: number): void {
    this.mes = mes;
    this.ano = this.anoSeletor;
    this.seletorAberto = false;
    this.recarregarDoInicio();
  }

  public limparFiltros(): void {
    this.mes = new Date().getMonth() + 1;
    this.ano = new Date().getFullYear();
    this.recarregarDoInicio();
  }

  private recarregarDoInicio(): void {
    this.paginaAtual = 1;
    this.itens = [];
    this.ultimaPagina = false;
    this.selecionados.clear();
    this.carregar();
  }

  public proximaPagina(): void {
    this.paginaAtual++;
    this.carregar();
  }

  public carregar(): void {
    this.isLoading = true;
    this.fciService.getAll({
      mes: this.mes,
      ano: this.ano,
      pagina: this.paginaAtual,
      tamanhoPagina: this.tamanhoPagina,
    }).subscribe({
      next: resp => {
        this.isLoading = false;
        if (!resp?.isError) {
          const novos = resp.listaFci ?? [];
          this.itens = this.paginaAtual === 1 ? novos : [...this.itens, ...novos];
          this.totalRegistros = resp.totalRegistros ?? 0;
          this.ultimaPagina = this.itens.length >= this.totalRegistros;
        } else {
          this.alertService.show('Erro', resp.errorDescription, AlertType.error);
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar as FCIs.', AlertType.error);
      },
    });
  }

  public incluir(): void {
    this.router.navigate(['/suprimentos/fci/cadastrar']);
  }

  public editar(item: FciListStruct): void {
    this.router.navigate(['/suprimentos/fci/editar', item.idFci]);
  }

  public statusProtocoloClass(item: FciListStruct): string {
    return item.protocoloRecepcao ? 'status-badge badge-verde' : 'status-badge badge-amarelo';
  }

  public exportar(item: FciListStruct): void {
    const idEmpresa = this.authService.getUserInfos()?.idEmpresa;
    if (!idEmpresa) {
      this.alertService.show('Erro', 'Não foi possível identificar a empresa logada.', AlertType.error);
      return;
    }

    this.empresaService.obterDadosPorId(idEmpresa).subscribe({
      next: empresaResp => {
        if (empresaResp.isError || !empresaResp.empresa) {
          this.alertService.show('Erro', empresaResp.errorDescription || 'Não foi possível obter os dados da empresa.', AlertType.error);
          return;
        }

        const empresa = empresaResp.empresa;
        this.fciService.exportar(item.idFci, {
          cnpj: empresa.cnpj,
          nomeEmpresa: empresa.nomeEmpresa,
          inscricaoEstadual: empresa.inscricaoEstadual,
          cep: empresa.cep,
          logradouro: empresa.logradouro,
          numero: empresa.numero,
          bairro: empresa.bairro,
          municipio: empresa.municipio,
          codIbgeMunicipio: empresa.codIbgeMunicipio,
          uf: empresa.uf,
        }).subscribe({
          next: blob => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `FCI_${empresa.cnpj}_${item.idFci}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1500);
            this.alertService.show(
              'Arquivo exportado',
              'Após transmitir no portal do SEFAZ, retorne aqui e informe o Protocolo de Recepção e o Código FCI para vincular ao produto.',
              AlertType.success,
            );
          },
          error: () => this.alertService.show('Erro', 'Não foi possível exportar o arquivo.', AlertType.error),
        });
      },
      error: () => this.alertService.show('Erro', 'Não foi possível obter os dados da empresa.', AlertType.error),
    });
  }

  public excluir(item: FciListStruct): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Excluir FCI',
        description: `Excluir a FCI nº #${item.idFci}? Esta ação não pode ser desfeita.`,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.fciService.excluir(item.idFci).subscribe({
        next: resp => {
          if (!resp?.isError) {
            this.alertService.show('Sucesso', 'FCI excluída.', AlertType.success);
            this.recarregarDoInicio();
          } else {
            this.alertService.show('Erro', resp.errorDescription, AlertType.error);
          }
        },
        error: () => this.alertService.show('Erro', 'Não foi possível excluir.', AlertType.error),
      });
    });
  }
}
